import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar as Rade } from 'react-chartjs-2';
import { FaCamera } from 'react-icons/fa';

import './style.css';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const Radar = ({
  data = [],
  imageUrl = '',
  minMax,
  mid,
  height = '600px',
  width = '600px',
  imgW = '100px',
  imgH = '100px',
  imgTop = '34%'
}) => {
  const safeData = Array.isArray(data) ? data : [];
  const safeMinMax = Array.isArray(minMax) ? minMax : [];

  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [imgCenter, setImgCenter] = useState(null);

  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    if (!chart || !container) return;

    // Центр радара внутри канваса (легенда сверху смещает его вниз от центра канваса)
    const { xCenter, yCenter } = chart.scales.r;
    const canvasRect = chart.canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setImgCenter({
      left: canvasRect.left - containerRect.left + xCenter,
      top: canvasRect.top - containerRect.top + yCenter,
    });
  }, [data, minMax, height, width]);

  const myPerf = useMemo(() => {
    if (!safeData.length) return [];

    // Новая структура: [{ first_level_title, first_level_id, user_results: [...] }, ...]
    if (safeData[0]?.first_level_id && safeData[0]?.user_results) {
      return safeData
        .map(category => {
          const user = category.user_results?.find(u => u.mid === mid);
          if (!user) return null;
          return {
            mid,
            title: category.first_level_title,
            points: user.last_level_points || 0,
            last_level_count: user.last_level_count,
          };
        })
        .filter(Boolean);
    }

    // Старая структура
    return safeData.filter(el => el?.mid === mid);
  }, [safeData, mid]);

  const myPerfSort = useMemo(() => {
    if (!safeMinMax.length || !myPerf.length) return [];
    return safeMinMax.map(el => myPerf.find(_el => _el?.title === el?.title) || {});
  }, [safeMinMax, myPerf]);

  const maxValue = useMemo(() => {
    if (!safeMinMax.length) return { max: 100 };
    return safeMinMax.reduce((acc, curr) => parseFloat(acc.max) > parseFloat(curr.max) ? acc : curr);
  }, [safeMinMax]);

  const minValue = useMemo(() => {
    if (!safeMinMax.length) return { min: 0 };
    return safeMinMax.reduce((acc, curr) => parseFloat(acc.min) < parseFloat(curr.min) ? acc : curr);
  }, [safeMinMax]);

  const getNormalizedValue = (val, padding = 27, max = 100) => {
    const min = parseFloat(minValue.min);
    const maxV = parseFloat(maxValue.max);
    const value = ((parseFloat(val) - min) / (maxV - min)) * (max - padding) + padding;
    return isNaN(value) ? padding : value;
  };

  const RadarData = {
    labels: myPerfSort.map(el => el?.title),
    datasets: [
      {
        label: "Ваши показатели",
        backgroundColor: "rgba(34, 202, 236, .2)",
        borderColor: "rgba(34, 202, 236, 1)",
        pointBackgroundColor: "rgba(34, 202, 236, 1)",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(34, 202, 236, 1)",
        data: myPerfSort.map(el => getNormalizedValue(el?.points)),
        custom: myPerfSort.map(el => el?.points)
      },
      {
        label: "Минимальные показатели",
        backgroundColor: "rgba(241, 139, 36, .2)",
        borderColor: "rgba(241, 139, 36, 1)",
        pointBackgroundColor: "rgba(241, 139, 36, 1)",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(241, 139, 36, 1)",
        data: safeMinMax.map(el => getNormalizedValue(el?.min)),
        custom: safeMinMax.map(el => el?.min)
      },
      {
        label: "Максимальные показатели",
        backgroundColor: "rgba(107, 60, 138, .2)",
        borderColor: "rgba(107, 60, 138, 1)",
        pointBackgroundColor: "rgba(107, 60, 138, 1)",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(107, 60, 138, 1)",
        data: safeMinMax.map(el => getNormalizedValue(el?.max)),
        custom: safeMinMax.map(el => el?.max)
      }
    ]
  };

  const RadarDataSolo = {
    labels: myPerf.map(el => el.title),
    datasets: [
      {
        label: "Ваши показатели",
        backgroundColor: "rgba(34, 202, 236, .2)",
        borderColor: "rgba(34, 202, 236, 1)",
        pointBackgroundColor: "rgba(34, 202, 236, 1)",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "rgba(34, 202, 236, 1)",
        data: myPerf.map(el => getNormalizedValue(el?.points)),
        custom: myPerf.map(el => el?.points)
      }
    ]
  };

  const optionss = {
    responsive: false,
    maintainAspectRatio: false,
    plugins: {
      title: {
        color: "rgba(0, 0, 0, 1)",
        display: true,
        text: '',
        size: 20,
      },
      tooltip: {
        mode: "index",
        caretSize: 3,
        callbacks: {
          label: function (context) {
            return context.dataset.custom?.[context.dataIndex] ?? '0';
          }
        }
      },
      legend: {
        labels: {
          color: "rgba(0, 0, 0, 1)",
          font: {
            size: 16
          }
        }
      },
    },
    scales: {
      r: {
        grid: {
          circular: true,
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: {
          beginAtZero: false,
          font: {
            size: 10
          },
          display: false
        }
      }
    }
  };

  return (
    <div className="infoBlock" ref={containerRef}>
      <div
        className={minMax?.length ? "studentImg" : "studentImgSolo"}
        style={{
          width: imgW,
          height: imgH,
          ...(imgCenter
            ? { left: imgCenter.left, top: imgCenter.top, transform: 'translate(-50%, -50%)' }
            : { bottom: imgTop }),
        }}
      >
        {imageUrl
          ? <img src={imageUrl} alt="Фото" />
          : <div className="noPhoto">
              <FaCamera size="50%" color="#5c76e3" />
            </div>
        }
      </div>
      <div>
        <Rade
          ref={chartRef}
          data={safeMinMax.length ? RadarData : RadarDataSolo}
          options={optionss}
          height={height}
          width={width}
          id="race"
        />
      </div>
    </div>
  );
};

export { Radar };
